// Formatierung.
//
// Die Daten kommen als Unix-Sekunden in UTC. Alles, was im Chart erscheint, wird
// in die Zone des Browsers umgerechnet: Achse, Leseleiste, Stand. Die UTC-Zeit
// wird zusätzlich genannt, wo der genaue Zeitpunkt zählt — beim Tages-Cut und
// bei den Zinsentscheidungen.
//
// Der Wirksamkeitstag eines beschlossenen Schritts ist Mitternacht UTC. Er darf
// nur als Datum in UTC erscheinen: lokal westlich von UTC gelesen zeigt er den
// Vortag.

import type { Lang } from "./types";

const LOCALE: Record<Lang, string> = { de: "de-DE", en: "en-US" };
export const NY_TZ = "America/New_York";
export const UTC_TZ = "UTC";

export function locale(lang: string): string {
    return LOCALE[lang as Lang] ?? LOCALE.de;
}

/** Unix-Sekunden -> Date */
export function tsDate(sec: number): Date {
    return new Date(sec * 1000);
}

const cache = new Map<string, Intl.DateTimeFormat>();

function fmt(lang: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
    const key = locale(lang) + JSON.stringify(opts);
    let f = cache.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat(locale(lang), opts);
        cache.set(key, f);
    }
    return f;
}

// --- lokale Zone --------------------------------------------------------------

/** 09.09.2026 */
export function localDate(d: Date, lang: string): string {
    return fmt(lang, { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
}

/** 09.09.2026, 03:00 */
export function localDateTime(d: Date, lang: string): string {
    return fmt(lang, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
}

/** 09.09. */
export function localDayShort(d: Date, lang: string): string {
    return fmt(lang, { day: "2-digit", month: "2-digit" }).format(d);
}

/** 9. Sep — Achsenticks in kurzen Fenstern */
export function tickDay(d: Date, lang: string): string {
    return fmt(lang, { day: "numeric", month: "short" }).format(d);
}

/** Sep 26 — Achsenticks in langen Fenstern */
export function tickMonth(d: Date, lang: string): string {
    return fmt(lang, { month: "short", year: "2-digit" }).format(d);
}

/** Sep 2026 */
export function monthYear(d: Date, lang: string): string {
    return fmt(lang, { month: "short", year: "numeric" }).format(d);
}

export function localZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return "UTC";
    }
}

// --- UTC ----------------------------------------------------------------------

/** 16.09.2026 — Wirksamkeitstag, immer in UTC */
export function utcDate(d: Date, lang: string): string {
    return fmt(lang, {
        timeZone: UTC_TZ,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(d);
}

/** 16.09. — Wirksamkeitstag kurz, immer in UTC */
export function utcDayShort(d: Date, lang: string): string {
    return fmt(lang, { timeZone: UTC_TZ, day: "2-digit", month: "2-digit" }).format(d);
}

/** 09.09.2026, 00:00 — derselbe Zeitpunkt, in UTC gelesen */
export function utcDateTime(d: Date, lang: string): string {
    return fmt(lang, {
        timeZone: UTC_TZ,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
}

// --- New York -----------------------------------------------------------------

/** 09.09.2026, 21:00 — derselbe Zeitpunkt, in New Yorker Zeit gelesen */
export function nyDateTime(d: Date, lang: string): string {
    return fmt(lang, {
        timeZone: NY_TZ,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
}

// --- Zahlen -------------------------------------------------------------------

/** 450 bp -> "4,50 %" */
export function pct(bp: number, lang: string, digits = 2): string {
    const n = new Intl.NumberFormat(locale(lang), {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(bp / 100);
    return n + " %";
}

/** -25 -> "−25 bps", 0 -> "Halten" / "Hold" */
export function deltaLabel(bp: number, lang: string): string {
    if (bp === 0) return lang === "en" ? "Hold" : "Halten";
    const sign = bp < 0 ? "\u2212" : "+";
    return sign + Math.abs(bp) + " bps";
}

export function binLabel(bp: number, j: number, count: number, lang: string): string {
    const base = deltaLabel(bp, lang);
    if (j === count - 1 && bp > 0) return "\u2265 " + base;
    if (j === 0 && bp < 0) return "\u2264 " + base;
    return base;
}

/** -18.25 -> "−18 bps" */
export function deltaCompact(bp: number, lang: string): string {
    const r = Math.round(bp);
    if (r === 0) return lang === "en" ? "no change" : "unverändert";
    const sign = r < 0 ? "\u2212" : "+";
    return sign + Math.abs(r) + " bps";
}

export function pctInt(pPerMille: number, lang: string): string {
    return new Intl.NumberFormat(locale(lang)).format(Math.round(pPerMille / 10)) + " %";
}